import { useState, useMemo } from "react";
import { useMode } from "@/context/ModeContext";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, User, AlertCircle, Search } from "lucide-react";
import SyncIndicator from "@/components/SyncIndicator";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { MobileHeader } from "@/components/MobileHeader";
import { usePagination } from "../hooks/usePagination";
import TablePagination from "../components/TablePagination";
import { useQuota } from "../hooks/useQuota";
import PropTypes from 'prop-types';
import { AddPatientDialog } from "@/components/AddPatientDialog";
import { getDisplayAge } from "../utils/patient";


export default function Patients({ token, onLogout }) {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const doctorId = localStorage.getItem('user_id');
  const { activeMode } = useMode();
  const patientsQuota = useQuota('patients');

  const patients = useLiveQuery(async () => {
    const allPatients = await db.patients.filter(p => !p.deleted).toArray();

    if (!activeMode || activeMode === 'everything') {
      return allPatients;
    }

    if (activeMode === 'personal') {
      const myPatientIds = new Set(
        await db.patient_doctor_links
          .where('user_id').equals(doctorId)
          .filter(l => !l.clinic_id || l.clinic_id === 'personal')
          .toArray()
          .then(links => links.map(l => l.patient_id))
      );
      return allPatients.filter(p => myPatientIds.has(p.id));
    }

    const clinicAptPatientIds = new Set(
      await db.appointments
        .where('clinic_id').equals(activeMode)
        .toArray()
        .then(apts => apts.map(a => a.patient_id))
    );

    const linkedPatientIds = new Set(
      await db.patient_doctor_links
        .where('clinic_id').equals(activeMode)
        .toArray()
        .then(links => links.map(l => l.patient_id))
    );

    return allPatients.filter(p => clinicAptPatientIds.has(p.id) || linkedPatientIds.has(p.id));
  }, [activeMode, doctorId]) || [];

  const filteredPatients = useMemo(() => {
    if (!searchTerm) return patients;
    const term = searchTerm.toLowerCase();
    return patients.filter(p =>
      p.name.toLowerCase().includes(term) ||
      p.phone.includes(term)
    );
  }, [patients, searchTerm]);

  const pagination = usePagination(filteredPatients, {
    initialPageSize: 15,
    isMobile: window.innerWidth < 768
  });

  return (
    <div className="page-container" data-testid="patients-page">
      <Sidebar active="patients" onLogout={onLogout} />
      <div className="main-content">
        <div className="header hidden md:flex">
          <h1>Patients</h1>
          <Button
            data-testid="add-patient-button"
            onClick={() => {
              if (!patientsQuota.allowed) {
                toast.error("Patient limit reached. Please upgrade your plan.");
                return;
              }
              setShowDialog(true)
            }}
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Plus size={20} style={{ marginRight: '0.5rem' }} />
              Add Patient
            </div>
          </Button>
        </div>

        <MobileHeader title="Patients">
          <Button
            className="w-full mt-2"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
            onClick={() => {
              if (!patientsQuota.allowed) {
                toast.error("Patient limit reached. Please upgrade your plan.");
                return;
              }
              setShowDialog(true)
            }}>
            <Plus size={20} className="mr-2" />
            Add Patient
          </Button>
        </MobileHeader>
        <div className="search-bar mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search patients by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="patient-search-input"
            />
          </div>
        </div>

        {patients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><User /></div>
            <h3>No patients yet</h3>
            <p>Add your first patient to get started</p>
          </div>
        ) : (
          <div className="card">
            {/* Desktop Table View */}
            <div className="table-container desktop-only">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Phone</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.items.map((patient) => (
                    <tr key={patient.id} data-testid={`patient-row-${patient.id}`} className={patient._synced === false ? 'entity-row-unsynced' : ''}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {patient.name}
                          {patient.sync_status === 'failed' ? (
                            <div title={patient.sync_error} style={{ color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                              <AlertCircle size={16} />
                            </div>
                          ) : (
                            <SyncIndicator show={patient._synced === false} />
                          )}
                        </div>
                      </td>
                      <td>{getDisplayAge(patient) ? `${getDisplayAge(patient)}` : "—"}</td>
                      <td style={{ textTransform: 'capitalize' }}>{patient.gender}</td>
                      <td>{patient.phone}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button
                            data-testid={`view-patient-${patient.id}`}
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/patients/${patient.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-only">
              {pagination.items.map((patient) => (
                <div
                  key={patient.id}
                  data-testid={`patient-card-${patient.id}`}
                  className={patient._synced === false ? 'entity-card-unsynced' : ''}
                  style={{
                    padding: '1rem',
                    background: patient._synced === false ? undefined : '#f8fafc',
                    borderRadius: '8px',
                    marginBottom: '0.75rem',
                    borderLeft: patient._synced === false ? undefined : '3px solid #0ea5e9'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {patient.name}
                        {patient.sync_status === 'failed' ? (
                          <div title={patient.sync_error} style={{ color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                            <AlertCircle size={16} />
                          </div>
                        ) : (
                          <SyncIndicator show={patient._synced === false} />
                        )}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        {getDisplayAge(patient) ? `${getDisplayAge(patient)} yrs` : "— yrs"} • {patient.gender}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.75rem' }}>
                    📞 {patient.phone}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <Button
                      data-testid={`view-patient-${patient.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      style={{ flex: 1 }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <TablePagination
              pagination={pagination}
              isMobile={window.innerWidth < 768}
            />
          </div>
        )}

        <AddPatientDialog
          open={showDialog}
          onOpenChange={setShowDialog}
          token={token}
        />
      </div>
    </div >
  );
}

Patients.propTypes = {
  token: PropTypes.string,
  onLogout: PropTypes.func
};